const adminApiBaseUrl = (
  process.env.REACT_APP_PAYMENT_API_URL ||
  'https://southamerica-east1-coloniadeferiasss.cloudfunctions.net/paymentApi'
).replace(/\/$/, '');

export interface ItemEvento {
  id: string;
  nome: string;
  valor: number;
  permiteQuantidade?: boolean;
}

export interface ItemEventoSelecionado {
  id: string;
  quantidade: number;
}

export interface CriancaEvento {
  id: string;
  nomeCompleto: string;
  turma?: string;
}

export interface PagamentoEvento {
  id: string;
  status: string;
  billingType: 'PIX' | 'GRATUITO';
  value: number;
  invoiceUrl?: string;
  pixQrCode?: string;
  pixQrCodeUrl?: string;
  pixExpirationDate?: string;
}

export interface CompraEvento {
  id: string;
  nomeResponsavel: string;
  telefoneResponsavel?: string;
  inscricaoId: string;
  nomeCrianca: string;
  turma?: string;
  itens: (ItemEvento & { quantidade: number })[];
  valorBase: number;
  valorTotal: number;
  asaasPaymentId?: string;
  paymentStatus?: string;
  status: 'pendente' | 'confirmado';
  pago: boolean;
  invoiceUrl?: string;
  createdAt?: unknown;
}

export const fetchItensEvento = async (): Promise<ItemEvento[]> => {
  const response = await fetch(`${adminApiBaseUrl}/evento/itens`);
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload.success) throw new Error(payload.error || 'Não foi possível carregar os itens do evento.');
  return Array.isArray(payload.itens) ? payload.itens : [];
};

export const fetchCriancasEvento = async (): Promise<CriancaEvento[]> => {
  const response = await fetch(`${adminApiBaseUrl}/evento/criancas`);
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload.success) throw new Error(payload.error || 'Não foi possível carregar a lista de crianças.');
  return Array.isArray(payload.criancas) ? payload.criancas : [];
};

export const criarCompraEvento = async (dados: {
  nomeResponsavel: string;
  cpfResponsavel: string;
  inscricaoId: string;
  itens: ItemEventoSelecionado[];
}): Promise<{ compraId: string; payment: PagamentoEvento }> => {
  const response = await fetch(`${adminApiBaseUrl}/evento/compras`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(dados)
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload.success) throw new Error(payload.error || 'Não foi possível gerar o pagamento.');
  return { compraId: payload.compraId, payment: payload.payment };
};

export interface CompraEventoDetalhe {
  compraId: string;
  nomeResponsavel: string;
  nomeCrianca: string;
  turma?: string;
  pago: boolean;
  payment: PagamentoEvento;
}

export const buscarCompraEvento = async (compraId: string): Promise<CompraEventoDetalhe> => {
  const response = await fetch(`${adminApiBaseUrl}/evento/compras/${encodeURIComponent(compraId)}`);
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload.success) throw new Error(payload.error || 'Não foi possível carregar o pagamento.');
  return payload;
};

export const consultarPagamentoEvento = async (
  paymentId: string
): Promise<{ id: string; status: string; value: number; invoiceUrl?: string }> => {
  const response = await fetch(`${adminApiBaseUrl}/status?paymentId=${encodeURIComponent(paymentId)}`);
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload.success || !payload.payment) {
    throw new Error(payload.error || 'Não foi possível consultar o pagamento.');
  }
  return payload.payment;
};

export const confirmarCompraEvento = async (
  compraId: string,
  payment: { id: string; status: string; invoiceUrl?: string }
): Promise<'pendente' | 'confirmado'> => {
  const response = await fetch(`${adminApiBaseUrl}/evento/compras/${encodeURIComponent(compraId)}/confirmar`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ payment })
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload.success) throw new Error(payload.error || 'Não foi possível confirmar o pagamento.');
  return payload.status;
};

// ---- Admin ----

export const fetchItensEventoAdmin = fetchItensEvento;

export const salvarItensEvento = async (itens: ItemEvento[]): Promise<ItemEvento[]> => {
  const response = await fetch(`${adminApiBaseUrl}/admin/evento/itens`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ itens })
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload.success) throw new Error(payload.error || 'Não foi possível salvar os itens.');
  return Array.isArray(payload.itens) ? payload.itens : [];
};

export const fetchComprasEvento = async (): Promise<CompraEvento[]> => {
  const response = await fetch(`${adminApiBaseUrl}/admin/evento/compras`);
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload.success) throw new Error(payload.error || 'Não foi possível carregar as inscrições do evento.');
  return Array.isArray(payload.compras) ? payload.compras : [];
};

export interface CartelaBingo {
  id: string;
  compraId: string;
  nomeCrianca: string;
  nomeResponsavel: string;
  turma?: string;
  fotoUrl?: string;
  numeros: { B: number[]; I: number[]; N: number[]; G: number[]; O: number[] };
  criadoEmMs?: number;
}

export const fetchCartelasBingo = async (): Promise<CartelaBingo[]> => {
  const response = await fetch(`${adminApiBaseUrl}/admin/evento/cartelas-bingo`);
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload.success) throw new Error(payload.error || 'Não foi possível carregar as cartelas de bingo.');
  return Array.isArray(payload.cartelas) ? payload.cartelas : [];
};

export const verificarPagamentosEvento = async (): Promise<number> => {
  const response = await fetch(`${adminApiBaseUrl}/admin/evento/compras/verificar`, { method: 'POST' });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload.success) throw new Error(payload.error || 'Não foi possível verificar os pagamentos.');
  return Number(payload.atualizados || 0);
};
